// ============================================================
// TATHYA DMS - Column Manager
// Version: 1.1.0
// Date: 2026-04-22
// Change (v1.1): added 'pdf' to the data type selector so users
//   can create PDF attachment columns. PDF columns take no extra
//   config — the existing formula/text conditional blocks don't
//   apply to them and fall through correctly.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDatabase, addColumn, updateColumn, deleteColumn
} from '../../services/api';

const DATA_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time (clock — e.g. 9:30 AM)' },
  { value: 'hour', label: 'Duration (hours/minutes — e.g. 1:58)' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'formula', label: 'Formula' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'email', label: 'Email Address' },
  { value: 'pdf', label: 'PDF (file attachment)' }
];

const ColumnManager = () => {
  const { org_slug, db_id } = useParams();
  const navigate = useNavigate();

  const [database, setDatabase] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCol, setEditingCol] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', data_type: 'text' });
  const [newCol, setNewCol] = useState({ label: '', data_type: 'text', formula_expression: '', allowed_values_text: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);

  // Convert the multiline textarea into an array (one value per line,
  // blanks stripped, duplicates removed). Both UI and server do this
  // normalisation; the server is source of truth.
  const parseAllowedList = (text) => {
    if (!text) return [];
    const seen = new Set();
    return text.split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l !== '')
      .filter(l => seen.has(l) ? false : (seen.add(l), true));
  };

  // Serialise an allowed_values column value (JSON string from the
  // backend) back to multiline text for the textarea.
  const allowedToText = (raw) => {
    if (!raw) return '';
    try {
      const parsed = Array.isArray(raw) ? raw : JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.join('\n') : '';
    } catch { return ''; }
  };

  const loadData = async () => {
    try {
      const res = await getDatabase(org_slug, db_id);
      setDatabase(res.data);
      setColumns(res.data.DbColumns || []);
    } catch (err) {
      setError('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [org_slug, db_id]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCol.label.trim()) return setError('Column name required');
    if (newCol.data_type === 'formula' && !newCol.formula_expression?.trim()) {
      return setError('Formula expression required for formula columns');
    }
    setSaving(true);
    try {
      const payload = {
        label: newCol.label,
        data_type: newCol.data_type,
        formula_expression: newCol.formula_expression || null
      };
      // Only send allowed_values for text columns
      if (newCol.data_type === 'text') {
        payload.allowed_values = parseAllowedList(newCol.allowed_values_text);
      }
      const res = await addColumn(org_slug, db_id, payload);
      setColumns(prev => [...prev, res.data]);
      setNewCol({ label: '', data_type: 'text', formula_expression: '', allowed_values_text: '' });
      setShowAdd(false);
      setSuccess('Column added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add column');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (colId) => {
    setSaving(true);
    try {
      const payload = {
        label: editForm.label,
        data_type: editForm.data_type,
        formula_expression: editForm.formula_expression || null
      };
      // Only send allowed_values for text columns; server clears it for other types.
      if (editForm.data_type === 'text') {
        payload.allowed_values = parseAllowedList(editForm.allowed_values_text || '');
      }
      const res = await updateColumn(org_slug, db_id, colId, payload);
      setColumns(prev => prev.map(c => c.id === colId ? res.data : c));
      setEditingCol(null);
      setSuccess('Column updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (colId, label) => {
    if (!window.confirm(`Delete column "${label}"? All data in this column will be lost.`)) return;
    try {
      await deleteColumn(org_slug, db_id, colId);
      setColumns(prev => prev.filter(c => c.id !== colId));
      setSuccess('Column deleted');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) return <div style={styles.center}>Loading...</div>;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <button className="btn btn-secondary"
            onClick={() => navigate(`/${org_slug}/databases/${db_id}`)}
            style={{ marginBottom: '8px', fontSize: '12px' }}>
            ← Back to {database?.name}
          </button>
          <h1 style={styles.title}>Manage Columns</h1>
          <p style={styles.subtitle}>
            {database?.db_number} · {database?.name} · {columns.length} columns
          </p>
        </div>
        <button className="btn btn-primary"
          onClick={() => { setShowAdd(!showAdd); setError(''); }}>
          {showAdd ? 'Cancel' : '+ Add Column'}
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
          <button onClick={() => setError('')} style={styles.closeBtn}>✕</button>
        </div>
      )}
      {success && (
        <div style={styles.successBox}>
          {success}
          <button onClick={() => setSuccess('')} style={styles.closeBtn}>✕</button>
        </div>
      )}

      {/* Add Column Form */}
      {showAdd && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <h3 style={styles.formTitle}>Add New Column</h3>
          <form onSubmit={handleAdd}>
            <div style={styles.formRow}>
              <div className="form-group" style={{ flex: 2 }}>
                <label>Column Name *</label>
                <input
                  type="text"
                  value={newCol.label}
                  onChange={e => setNewCol({...newCol, label: e.target.value})}
                  placeholder="Enter column name"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Data Type</label>
                <select
                  value={newCol.data_type}
                  onChange={e => setNewCol({...newCol, data_type: e.target.value, formula_expression: ''})}
                >
                  {DATA_TYPES.map(dt => (
                    <option key={dt.value} value={dt.value}>{dt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {newCol.data_type === 'formula' && (
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label>Formula Expression *</label>
                <input
                  type="text"
                  value={newCol.formula_expression || ''}
                  onChange={e => setNewCol({...newCol, formula_expression: e.target.value})}
                  placeholder="e.g. [Quantity] * [Unit Price]"
                  id="formula-input-new"
                />
                <p style={{ fontSize: '11px', color: '#595959', marginTop: '4px' }}>
                  Use [Column Name] to reference other columns. Supports + - * / ( ) operators.
                  Example: [Quantity] * [Unit Price] * (1 - [Discount] / 100)
                </p>
                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {columns.filter(c => c.data_type !== 'formula').map(c => (
                    <button key={c.id} type="button"
                      onClick={() => {
                        const input = document.getElementById('formula-input-new');
                        const newVal = (newCol.formula_expression || '') + '[' + c.label + ']';
                        setNewCol({...newCol, formula_expression: newVal});
                        setTimeout(() => {
                          if (input) {
                            input.focus();
                            input.setSelectionRange(newVal.length, newVal.length);
                          }
                        }, 10);
                      }}
                      style={{ background: '#D6E4F0', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', color: '#1B3A5C' }}>
                      + [{c.label}]
                    </button>
                  ))}
                </div>
              </div>
            )}
            {newCol.data_type === 'text' && (
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label>Allowed values (optional, one per line)</label>
                <textarea
                  value={newCol.allowed_values_text || ''}
                  onChange={e => setNewCol({ ...newCol, allowed_values_text: e.target.value })}
                  placeholder={'YES\nNO'}
                  rows={4}
                  style={{
                    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
                    borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace',
                    resize: 'vertical', outline: 'none'
                  }}
                />
                <p style={{ fontSize: '11px', color: '#595959', marginTop: '4px' }}>
                  If set, cells in this column will show a dropdown and only accept these values.
                  Leave blank for free text entry. Examples: YES / NO, or Active / Inactive / Pending.
                </p>
              </div>
            )}
            <div style={styles.formActions}>
              <button type="button" className="btn btn-secondary"
                onClick={() => setShowAdd(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary"
                disabled={saving}>
                {saving ? 'Adding...' : 'Add Column'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Columns Table */}
      <div className="card">
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Column Name</th>
              <th style={styles.th}>Data Type</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {columns.length === 0 ? (
              <tr>
                <td colSpan={4} style={styles.empty}>
                  No columns yet. Add the first column above.
                </td>
              </tr>
            ) : (
              columns.map((col, idx) => (

                <tr style={styles.tr}>
                  <td style={styles.td}>{idx + 1}</td>
                  <td style={styles.td}>
                    {editingCol === col.id ? (
                      <input
                        type="text"
                        value={editForm.label}
                        onChange={e => setEditForm({...editForm, label: e.target.value})}
                        style={styles.inlineInput}
                        autoFocus
                      />
                    ) : (
                      <span style={styles.colName}>{col.label}</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {editingCol === col.id ? (
                      <div>
                        <select
                          value={editForm.data_type}
                          onChange={e => setEditForm({...editForm, data_type: e.target.value})}
                          style={styles.inlineSelect}
                        >
                          {DATA_TYPES.map(dt => (
                            <option key={dt.value} value={dt.value}>{dt.label}</option>
                          ))}
                        </select>
                        {editForm.data_type === 'formula' && (
                          <div>
                            <input
                              type="text"
                              id="formula-input-edit"
                              value={editForm.formula_expression || ''}
                              onChange={e => setEditForm({...editForm, formula_expression: e.target.value})}
                              placeholder="e.g. [Col A] * [Col B]"
                              style={{...styles.inlineInput, marginTop: '4px', fontSize: '11px'}}
                            />
                            <div style={{marginTop:'6px', display:'flex', gap:'4px', flexWrap:'wrap'}}>
                              {columns.filter(c => c.id !== col.id && c.data_type !== 'formula').map(c => (
                                <button key={c.id} type="button"
                                  onClick={() => {
                                    const input = document.getElementById('formula-input-edit');
                                    const newVal = (editForm.formula_expression || '') + '[' + c.label + ']';
                                    setEditForm({...editForm, formula_expression: newVal});
                                    setTimeout(() => {
                                      if (input) { input.focus(); input.setSelectionRange(newVal.length, newVal.length); }
                                    }, 10);
                                  }}
                                  style={{background:'#D6E4F0',border:'none',borderRadius:'4px',padding:'2px 6px',fontSize:'10px',cursor:'pointer',color:'#1B3A5C'}}>
                                  +[{c.label}]
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {editForm.data_type === 'text' && (
                          <div style={{ marginTop: '6px' }}>
                            <textarea
                              value={editForm.allowed_values_text || ''}
                              onChange={e => setEditForm({ ...editForm, allowed_values_text: e.target.value })}
                              placeholder={'Allowed values, one per line (optional)'}
                              rows={3}
                              style={{
                                width: '100%', padding: '6px 8px', border: '1px solid #ddd',
                                borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace',
                                resize: 'vertical', outline: 'none'
                              }}
                            />
                            <div style={{ fontSize: '10px', color: '#595959', marginTop: '2px' }}>
                              Leave blank for free text. Fill to show a dropdown.
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span style={styles.typeBadge}>{col.data_type}</span>
                        {col.formula_expression && (
                          <div style={{fontSize:'10px', color:'#1A6B72', marginTop:'2px'}}>
                            ƒ {col.formula_expression}
                          </div>
                        )}
                        {col.data_type === 'text' && col.allowed_values && (
                          <div style={{ fontSize: '10px', color: '#1A6B72', marginTop: '2px' }}>
                            ▾ {(() => {
                              try {
                                const arr = JSON.parse(col.allowed_values);
                                return Array.isArray(arr) ? arr.join(' / ') : '';
                              } catch { return ''; }
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    {editingCol === col.id ? (
                      <div style={styles.actionBtns}>
                        <button className="btn btn-primary"
                          style={styles.smallBtn}
                          onClick={() => handleEdit(col.id)}
                          disabled={saving}>
                          {saving ? '...' : 'Save'}
                        </button>
                        <button className="btn btn-secondary"
                          style={styles.smallBtn}
                          onClick={() => setEditingCol(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div style={styles.actionBtns}>
                        <button className="btn btn-secondary"
                          style={styles.smallBtn}
                          onClick={() => {
                            setEditingCol(col.id);
                            setEditForm({
                              label: col.label,
                              data_type: col.data_type,
                              formula_expression: col.formula_expression || '',
                              allowed_values_text: allowedToText(col.allowed_values)
                            });
                          }}>
                          Edit
                        </button>
                        <button className="btn btn-danger"
                          style={styles.smallBtn}
                          onClick={() => handleDelete(col.id, col.label)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>

              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Formula column helper - shows below table when editing formula */}
      {editingCol && editForm.data_type === 'formula' && (
        <div style={{background:'#f0f9f8', border:'1px solid #b2dfdb', borderRadius:'8px', padding:'16px', marginTop:'16px'}}>
          <div style={{fontSize:'13px', color:'#1A6B72', marginBottom:'10px', fontWeight:'600'}}>
            📌 Insert column into formula:
          </div>
          <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
            {columns.filter(c => c.id !== editingCol && c.data_type !== 'formula').map(c => (
              <button key={c.id} type="button"
                onClick={() => {
                  const input = document.getElementById('formula-input-edit');
                  const newVal = (editForm.formula_expression || '') + '[' + c.label + ']';
                  setEditForm({...editForm, formula_expression: newVal});
                  setTimeout(() => {
                    if (input) { input.focus(); input.setSelectionRange(newVal.length, newVal.length); }
                  }, 10);
                }}
                style={{background:'#D6E4F0',border:'none',borderRadius:'6px',padding:'6px 12px',fontSize:'13px',cursor:'pointer',color:'#1B3A5C',fontWeight:'500'}}>
                + [{c.label}]
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1B3A5C' },
  subtitle: { color: '#595959', fontSize: '14px', marginTop: '4px' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#2E75B6' },
  errorBox: { background: '#FCE4D6', color: '#C0392B', padding: '10px 16px', borderRadius: '6px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  successBox: { background: '#E2F0D9', color: '#1E7B34', padding: '10px 16px', borderRadius: '6px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', opacity: 0.7 },
  formTitle: { fontSize: '16px', fontWeight: '600', color: '#1B3A5C', marginBottom: '16px' },
  formRow: { display: 'flex', gap: '16px', alignItems: 'flex-end' },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { padding: '10px 12px', background: '#1B3A5C', color: 'white', fontWeight: '600', textAlign: 'left', fontSize: '13px' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '10px 12px', fontSize: '13px', color: '#1B3A5C' },
  colName: { fontWeight: '500' },
  typeBadge: { background: '#D6E4F0', color: '#1B3A5C', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  actionBtns: { display: 'flex', gap: '6px' },
  smallBtn: { padding: '4px 10px', fontSize: '12px' },
  inlineInput: { width: '100%', padding: '6px 8px', border: '1px solid #2E75B6', borderRadius: '4px', fontSize: '13px', outline: 'none' },
  inlineSelect: { width: '100%', padding: '6px 8px', border: '1px solid #2E75B6', borderRadius: '4px', fontSize: '13px', outline: 'none' },
  empty: { textAlign: 'center', padding: '40px', color: '#595959' }
};

export default ColumnManager;
